import React, { useState, useEffect } from 'react';
import { PMT2Product } from '@/types/pmt2Types';
import { parseNumericValue } from '@/services/pmt2Utils';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableRow, TableHead, TableHeader } from '@/components/ui/table';
import { Skeleton } from '@/components/ui/skeleton';
import { cn } from "@/lib/utils"; // Import cn utility
import { CheckCircle, AlertCircle } from 'lucide-react'; // Import icons
import { fetchLatestOldValuesMap } from '@/services/pmt3OldValuesService';

interface ListingDashboardProps {
  product: PMT2Product | null;
}

type OldValuesMap = Record<string, string>;

const ListingDashboard: React.FC<ListingDashboardProps> = ({ product }) => {
  const [oldValuesMap, setOldValuesMap] = useState<OldValuesMap | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(true);

  // --- Formatting & Utility Helpers ---
  const formatInteger = (value: any): string => {
    const num = parseNumericValue(value);
    if (num === null) return '-';
    return Math.round(num).toLocaleString('en-US');
  };

  const formatYesNo = (value: any): string => {
      if (value === null || value === undefined || value === '') return '-';
      const str = String(value).toLowerCase();
      if (str === 'yes' || str === 'true' || str === '1') return 'Yes';
      if (str === 'no' || str === 'false' || str === '0') return 'No';
      return '-'; // Default if not recognized
  };

  const formatDefault = (value: any): string => {
    if (value === null || value === undefined || value === '') return '-';
    return String(value);
  };
  // --- End Helpers ---

  // Fetch Old Values Map
  useEffect(() => {
    const fetchMap = async () => {
      setIsLoading(true);
      setOldValuesMap(null);

      if (product?.companyName && product?.id) {
        try {
          const map = await fetchLatestOldValuesMap(product.companyName, product.id);
          setOldValuesMap(map);
        } catch (error) {
          console.error("Error fetching old values map for Listing:", error);
          setOldValuesMap(null);
        } finally {
          setIsLoading(false);
        }
      } else {
        setIsLoading(false);
      }
    };

    fetchMap();
  }, [product]);

  // Configuration for dashboard items
  const listingItemsConfig = [
    { label: 'Title Length', valueKey: 'Title Length', oldValueKey: 'Title Length', format: 'integer' },
    { label: 'Bullet Point Count', valueKey: 'Bullet Point Count', oldValueKey: 'Bullet Point Count', format: 'integer' },
    { label: 'Image Count', valueKey: 'Image Count', oldValueKey: 'Image Count', format: 'integer' },
    { label: 'Video Present', valueKey: 'Video Present', oldValueKey: 'Video Present', format: 'yesNo' },
    { label: 'Title A/B Test Days Ago', valueKey: 'Title A/B Test Days Ago', format: 'integer' }, // No oldValueKey
    { label: 'Bullet Points A/B Test Days Ago', valueKey: 'Bullet Points A/B Test Days Ago', format: 'integer' }, // No oldValueKey
    { label: 'Secondary Images A/B Test Days Ago', valueKey: 'Secondary Images A/B Test Days Ago', format: 'integer' }, // No oldValueKey
    { label: 'Main Image A/B Test Days Ago', valueKey: 'Main Image A/B Test Days Ago', format: 'integer' }, // No oldValueKey
  ];

  // Helper to format display values
  const formatDisplay = (value: any, formatType?: string): string => {
      if (value === null || value === undefined || value === '') return '-';
      switch (formatType) {
          case 'integer': return formatInteger(value);
          case 'yesNo': return formatYesNo(value);
          default: return formatDefault(value);
      }
  };

  if (!product) {
       return (
            <Card>
                <CardHeader><CardTitle className="text-2xl font-bold">Listing</CardTitle></CardHeader>
                <CardContent><p className="text-sm text-gray-500">Select a product to view Listing details.</p></CardContent>
            </Card>
        );
  }

  return (
    <Card className="max-w-2xl mx-auto"> {/* Reduced width by ~50% */}
      <CardHeader className="p-4">
        <CardTitle className="text-2xl font-bold">Listing</CardTitle>
      </CardHeader>
      <CardContent className="p-4">
        <Table>
            <TableHeader>
                <TableRow>
                    <TableHead className="w-2/3">Item</TableHead> {/* Adjusted width */}
                    <TableHead className="text-right">Value</TableHead>
                    <TableHead className="text-right">Old Value</TableHead>
                </TableRow>
            </TableHeader>
            <TableBody>
                {listingItemsConfig.map((item) => {
                    const currentValue = item.valueKey ? product[item.valueKey] : undefined;
                    const displayValue = formatDisplay(currentValue, item.format);

                    const hasOldValue = !!item.oldValueKey; // Check if old value is expected
                    const oldValue = hasOldValue ? oldValuesMap?.[item.oldValueKey!] : undefined;
                    const formattedOldValue = formatDisplay(oldValue, item.format);
                    const displayOldValueHighlight = hasOldValue && formattedOldValue !== '-' && String(displayValue) !== formattedOldValue;

                    return (
                        <TableRow key={item.label} className={cn(
                            // Add bottom border before 'Title A/B Test Days Ago'
                            {'border-b border-gray-200 dark:border-gray-700': item.label === 'Video Present'}
                        )}>
                            <TableCell className="font-medium py-1 px-2">{item.label}</TableCell> {/* Reduced padding */}
                            <TableCell className={cn("text-right py-1 px-2", // Reduced padding & conditional formatting
                                !isLoading && (
                                    // Title Length
                                    (item.label === 'Title Length' && parseNumericValue(currentValue) !== null && parseNumericValue(currentValue)! < 60) ? 'text-red-600 dark:text-red-400' :
                                    (item.label === 'Title Length' && parseNumericValue(currentValue) !== null && parseNumericValue(currentValue)! > 190) ? 'text-green-700 dark:text-green-400' :
                                    // Bullet Point Count
                                    (item.label === 'Bullet Point Count' && parseNumericValue(currentValue) === 5) ? 'text-green-700 dark:text-green-400' :
                                    (item.label === 'Bullet Point Count' && parseNumericValue(currentValue) !== null && parseNumericValue(currentValue)! < 5) ? 'text-red-600 dark:text-red-400' :
                                    // Image Count
                                    (item.label === 'Image Count' && parseNumericValue(currentValue) !== null && parseNumericValue(currentValue)! >= 7) ? 'text-green-700 dark:text-green-400' :
                                    (item.label === 'Image Count' && parseNumericValue(currentValue) !== null && parseNumericValue(currentValue)! < 7) ? 'text-red-600 dark:text-red-400' :
                                    // Video Present
                                    (item.label === 'Video Present' && displayValue === 'Yes') ? 'text-green-700 dark:text-green-400' :
                                    (item.label === 'Video Present' && displayValue === 'No') ? 'text-yellow-500 dark:text-yellow-400' :
                                    // A/B Test Days Ago (Common Logic)
                                    (item.label.includes('A/B Test Days Ago') && parseNumericValue(currentValue) !== null && parseNumericValue(currentValue)! >= 90) ? 'text-yellow-500 dark:text-yellow-400' :
                                    (item.label.includes('A/B Test Days Ago') && parseNumericValue(currentValue) !== null && parseNumericValue(currentValue)! < 90) ? 'text-green-700 dark:text-green-400' : ''
                                )
                            )}>
                                {isLoading ? <Skeleton className="h-4 w-16 float-right" /> : (
                                    <div className="flex items-center justify-end space-x-1">
                                        {/* Conditional Icon Logic */}
                                        {!isLoading && item.label === 'Title Length' && parseNumericValue(currentValue) !== null && parseNumericValue(currentValue)! < 60 && <AlertCircle size={16} />}
                                        {!isLoading && item.label === 'Title Length' && parseNumericValue(currentValue) !== null && parseNumericValue(currentValue)! > 190 && <CheckCircle size={16} />}
                                        {!isLoading && item.label === 'Bullet Point Count' && parseNumericValue(currentValue) === 5 && <CheckCircle size={16} />}
                                        {!isLoading && item.label === 'Bullet Point Count' && parseNumericValue(currentValue) !== null && parseNumericValue(currentValue)! < 5 && <AlertCircle size={16} />}
                                        {!isLoading && item.label === 'Image Count' && parseNumericValue(currentValue) !== null && parseNumericValue(currentValue)! >= 7 && <CheckCircle size={16} />}
                                        {!isLoading && item.label === 'Image Count' && parseNumericValue(currentValue) !== null && parseNumericValue(currentValue)! < 7 && <AlertCircle size={16} />}
                                        {!isLoading && item.label === 'Video Present' && displayValue === 'Yes' && <CheckCircle size={16} />}
                                        {!isLoading && item.label.includes('A/B Test Days Ago') && parseNumericValue(currentValue) !== null && parseNumericValue(currentValue)! < 90 && <CheckCircle size={16} />}
                                        <span>{displayValue}</span>
                                    </div>
                                )}
                            </TableCell>
                            <TableCell className="text-right py-1 px-2"> {/* Reduced padding */}
                                {isLoading ? <Skeleton className="h-4 w-16 float-right" /> : (
                                    hasOldValue ? ( // Only render content if old value is expected
                                        displayOldValueHighlight ? (
                                            <span className="bg-orange-100 dark:bg-orange-800 px-1.5 py-0.5 rounded text-orange-800 dark:text-orange-100">
                                                {formattedOldValue}
                                            </span>
                                        ) : (
                                            <span>-</span> // Show '-' if old value exists but matches or is invalid
                                        )
                                    ) : (
                                        <span></span> // Render empty span if no old value expected (handles A/B test rows)
                                    )
                                )}
                            </TableCell>
                        </TableRow>
                    );
                })}
            </TableBody>
        </Table>
      </CardContent>
    </Card>
  );
};

export default ListingDashboard;